import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Users, Calendar, Clock, UserPlus, ChevronRight, AlertTriangle, ShieldCheck, KeyRound } from "lucide-react";
import SecurityQuestionModal from "../components/SecurityQuestionModal";
import RecentNews from "../components/RecentNews";
import DashboardAnalytics from "../components/DashboardAnalytics";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecQ, setShowSecQ] = useState(false);
  const { doctor, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/dashboard/summary");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = [
    { label: "Total Patients", value: data?.total_patients ?? 0, icon: Users, color: "#0F2A5F", link: "/app/patients" },
    { label: "Today's Appointments", value: data?.todays_appointments ?? 0, icon: Calendar, color: "#2ea55f", link: "/app/appointments" },
    { label: "Upcoming Appointments", value: data?.upcoming_appointments ?? 0, icon: Clock, color: "#f5b800", link: "/app/appointments" },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Welcome, {doctor?.name?.split(" ")[0] || "Doctor"}</h1>
        <p className="text-slate-500 text-sm mt-1">Here's what's happening at your clinic today.</p>
      </div>

      {doctor?.status === "Overdue" && (
        <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            Your subscription dues are unpaid. Please clear them to avoid access restrictions.
          </div>
        </div>
      )}

      {doctor && !doctor.has_security_question && (
        <div className="mb-6 flex items-start justify-between gap-3 bg-[#E6F7F5] border border-[#14B8A6]/30 text-slate-800 rounded-md px-4 py-3">
          <div className="flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#14B8A6] mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">Enable self-service password reset</div>
              <div className="text-slate-600">Set a security question so you can reset your password if you forget it.</div>
            </div>
          </div>
          <button
            onClick={() => setShowSecQ(true)}
            className="text-xs bg-[#14B8A6] hover:bg-[#0F8A7D] text-white font-semibold px-3 py-1.5 rounded whitespace-nowrap inline-flex items-center gap-1"
          >
            <KeyRound size={12} /> Set now
          </button>
        </div>
      )}

      {doctor && doctor.has_security_question && (
        <div className="mb-6 flex items-center justify-between gap-3 text-sm text-slate-600">
          <div className="inline-flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#14B8A6]" />
            Security question is set for password recovery.
          </div>
          <button onClick={() => setShowSecQ(true)} className="text-[#0F2A5F] hover:underline text-xs font-semibold">
            Update
          </button>
        </div>
      )}

      <RecentNews />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => nav(c.link)}
            className="bg-white rounded-lg border border-slate-200 p-5 text-left hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between mb-3">
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${c.color}18`, color: c.color }}
              >
                <c.icon size={22} />
              </div>
              <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />
            </div>
            <div className="text-3xl font-bold text-slate-900">{loading ? "..." : c.value}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Recent Patients</h3>
          <button
            onClick={() => nav("/app/patients")}
            className="text-sm text-[#0F2A5F] hover:underline flex items-center gap-1"
          >
            View all <ChevronRight size={14} />
          </button>
        </div>
        {data?.recent_patients?.length ? (
          <div className="divide-y divide-slate-100">
            {data.recent_patients.map((p) => (
              <button
                key={p.id}
                onClick={() => nav(`/app/patients/${p.id}`)}
                className="w-full flex items-center justify-between py-3 hover:bg-slate-50 px-2 rounded-md transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#E6F7F5] text-[#0F2A5F] flex items-center justify-center font-semibold text-sm">
                    {p.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-slate-800 text-sm">{p.name}</div>
                    <div className="text-xs text-slate-500">UHID: {p.uhid} • {p.phone}</div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300" />
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <UserPlus size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm mb-3">No patients yet</p>
            <button
              onClick={() => nav("/app/patients")}
              className="bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white text-sm px-4 py-2 rounded-md transition-colors"
            >
              Add your first patient
            </button>
          </div>
        )}
      </div>

      <div className="mt-6">
        <DashboardAnalytics />
      </div>

      <SecurityQuestionModal
        open={showSecQ}
        initialQuestion={doctor?.secret_question || ""}
        onClose={() => setShowSecQ(false)}
        onSaved={() => refresh?.()}
      />
    </div>
  );
};

export default Dashboard;
